import { useCallback, useEffect, useState } from 'react';
import { CapturedSample, fetchDataSamples } from 'api/dataSamples';

export type SamplesState =
  | { status: 'draft' }
  | { status: 'loading' }
  // "forbidden": org viewers can't run actions. `message` is the server's detail, if any.
  | {
      status: 'error';
      reason: 'forbidden' | 'not_signed_in' | 'bad_response' | 'request';
      message?: string;
    }
  | { status: 'ready'; samples: CapturedSample[]; captureEnabled?: boolean };

function errorDetail(err: any): string | undefined {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'error' in detail)
    return String(detail.error);
  return undefined;
}

/** Captured samples for one data type, fetched while `enabled`. `refresh` always refetches. */
export function useDataSamples({
  enabled,
  token,
  integrationId,
  action,
  outputType,
}: {
  enabled: boolean;
  token?: string;
  integrationId?: string;
  action: string;
  outputType: string;
}) {
  const [state, setState] = useState<SamplesState>(
    integrationId ? { status: 'loading' } : { status: 'draft' },
  );
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    if (!integrationId) {
      setState({ status: 'draft' });
      return;
    }
    if (!token) {
      setState({ status: 'error', reason: 'not_signed_in' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchDataSamples(token, integrationId, action, outputType)
      .then((resp) => {
        if (cancelled) return;
        setState({
          status: 'ready',
          samples: Array.isArray(resp.samples[outputType])
            ? resp.samples[outputType]
            : [],
          captureEnabled: resp.capture_enabled[outputType],
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const reason =
          err?.response?.status === 403
            ? 'forbidden'
            : err?.name === 'DataSamplesFormatError'
              ? 'bad_response'
              : 'request';
        setState({ status: 'error', reason, message: errorDetail(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, token, integrationId, action, outputType, attempt]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, refresh };
}
